import { opfsBlobCache } from '@affine/core/utils/opfs-blob-cache';
import { BLOB_LOCAL_STORAGE_UNAVAILABLE_ERROR_CODE } from '@blocksuite/affine/sync';
import { BRAND_DISPLAY_NAME } from '@streamlain/brand';

const BLOB_RANGE_CHUNK_SIZE = 4 * 1024 * 1024;
const LOCAL_STORAGE_UNAVAILABLE_ERROR_NAME =
  'StreamLainBlobLocalStorageUnavailableError';

interface WorkspaceBlobInfo {
  key: string;
  mimeType: string;
  size: number;
  createdAt?: string;
}

export interface WorkspaceBlobMetadata {
  mime: string;
  size: number;
}

type WorkspaceBlobInfoResult =
  | { status: 'ok'; info: WorkspaceBlobInfo }
  | { status: 'terminal' }
  | { status: 'unavailable' };

export interface ResolveWorkspaceBlobOptions {
  workspaceId: string;
  key: string;
  fetchImpl?: typeof fetch;
}

type WorkspaceBlobFetchFactory = (workspaceId: string) => typeof fetch;
let workspaceBlobFetchFactory: WorkspaceBlobFetchFactory | undefined;

export function configureWorkspaceBlobFetchFactory(
  factory: WorkspaceBlobFetchFactory
): void {
  workspaceBlobFetchFactory = factory;
}

export class WorkspaceBlobLocalStorageUnavailableError extends Error {
  readonly code = BLOB_LOCAL_STORAGE_UNAVAILABLE_ERROR_CODE;

  constructor() {
    super(localStorageUnavailableMessage());
    this.name = LOCAL_STORAGE_UNAVAILABLE_ERROR_NAME;
  }
}

export function isWorkspaceBlobLocalStorageUnavailableError(
  error: unknown
): error is WorkspaceBlobLocalStorageUnavailableError {
  return (
    error instanceof Error &&
    (error.name === LOCAL_STORAGE_UNAVAILABLE_ERROR_NAME ||
      (error as { code?: unknown }).code ===
        BLOB_LOCAL_STORAGE_UNAVAILABLE_ERROR_CODE)
  );
}

function getBuildVersionHeaders(): Record<string, string> {
  return typeof BUILD_CONFIG !== 'undefined' && BUILD_CONFIG.appVersion
    ? { 'x-streamlain-version': BUILD_CONFIG.appVersion }
    : {};
}

function blobApiPath(workspaceId: string, key: string, suffix = ''): string {
  return `/api/workspaces/${encodeURIComponent(workspaceId)}/blobs/${encodeURIComponent(key)}${suffix}`;
}

async function fetchWorkspaceBlobInfo(
  workspaceId: string,
  key: string,
  fetchImpl: typeof fetch
): Promise<WorkspaceBlobInfoResult> {
  try {
    const response = await fetchImpl(blobApiPath(workspaceId, key, '/info'), {
      credentials: 'include',
      headers: getBuildVersionHeaders(),
    });
    if (!response.ok) {
      return isTerminalBlobInfoStatus(response.status)
        ? { status: 'terminal' }
        : { status: 'unavailable' };
    }
    const info = (await response.json()) as Partial<WorkspaceBlobInfo>;
    const { key: responseKey, mimeType, size, createdAt } = info;
    if (
      typeof responseKey !== 'string' ||
      typeof mimeType !== 'string' ||
      typeof size !== 'number' ||
      !Number.isSafeInteger(size) ||
      size < 0
    ) {
      return { status: 'unavailable' };
    }
    return {
      status: 'ok',
      info: {
        key: responseKey,
        mimeType,
        size,
        ...(typeof createdAt === 'string' ? { createdAt } : {}),
      },
    };
  } catch {
    return { status: 'unavailable' };
  }
}

function isTerminalBlobInfoStatus(status: number): boolean {
  return (
    status === 400 ||
    status === 401 ||
    status === 403 ||
    status === 404 ||
    status === 410
  );
}

export function workspaceBlobCacheToken(key: string, size: number): string {
  return `${key}:${size}`;
}

function cacheTokenForWorkspaceBlobInfo(info: WorkspaceBlobInfo): string {
  return workspaceBlobCacheToken(info.key, info.size);
}

function restoreBlobMimeType(blob: Blob, mime: string | undefined): Blob {
  const normalizedMime = mime?.trim();
  if (blob.type || !normalizedMime) return blob;
  return blob.slice(0, blob.size, normalizedMime);
}

function localStorageUnavailableMessage(): string {
  const details = opfsBlobCache.getAvailability();
  const base = `Local browser storage is blocked. ${BRAND_DISPLAY_NAME} needs site data to load images and PDFs.`;
  const isFirefox =
    typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent);
  const firefoxHint = isFirefox
    ? ` In Firefox, leave private or no-history mode, allow site data for ${BRAND_DISPLAY_NAME}, then reload.`
    : ` Allow site data for ${BRAND_DISPLAY_NAME}, then reload.`;
  const reason =
    details.available || !details.reason ? '' : ` (${details.reason})`;
  return `${base}${firefoxHint}${reason}`;
}

function assertOPFSAvailable(): void {
  if (!opfsBlobCache.available) {
    throw new WorkspaceBlobLocalStorageUnavailableError();
  }
}

const remoteValidations = new Set<string>();

function remoteValidationKey(workspaceId: string, key: string): string {
  return `${workspaceId}:${key}`;
}

function validateCachedBlobInBackground(
  workspaceId: string,
  key: string,
  fetchImpl: typeof fetch
): void {
  const validationKey = remoteValidationKey(workspaceId, key);
  if (remoteValidations.has(validationKey)) return;
  remoteValidations.add(validationKey);

  void (async () => {
    const localInfo = await opfsBlobCache.getInfo(workspaceId, key);
    if (localInfo?.uploadState === 'pending') {
      // A remote miss is expected until the durable upload outbox completes.
      // OPFS is authoritative during that window and must not be invalidated.
      return;
    }
    const infoResult = await fetchWorkspaceBlobInfo(
      workspaceId,
      key,
      fetchImpl
    );
    if (infoResult.status === 'terminal') {
      await opfsBlobCache.delete(workspaceId, key);
      return;
    }
    if (infoResult.status !== 'ok') return;

    const { info } = infoResult;
    const validated = await opfsBlobCache.getCompleteFile(workspaceId, key, {
      size: info.size,
      cacheToken: cacheTokenForWorkspaceBlobInfo(info),
    });
    if (!validated) {
      await opfsBlobCache.delete(workspaceId, key);
    }
  })()
    .catch(() => undefined)
    .finally(() => {
      remoteValidations.delete(validationKey);
    });
}

async function cacheRemoteBlobIntoOPFS(
  workspaceId: string,
  key: string,
  fetchImpl: typeof fetch,
  info?: WorkspaceBlobInfo
): Promise<File | null> {
  const remoteInfoResult =
    info !== undefined
      ? ({ status: 'ok', info } as const)
      : await fetchWorkspaceBlobInfo(workspaceId, key, fetchImpl);
  if (remoteInfoResult.status !== 'ok') return null;
  const remoteInfo = remoteInfoResult.info;
  if (!remoteInfo) return null;

  assertOPFSAvailable();
  try {
    const file = await opfsBlobCache.ensureCompleteFromRemote(
      workspaceId,
      key,
      {
        mime: remoteInfo.mimeType,
        size: remoteInfo.size,
        cacheToken: cacheTokenForWorkspaceBlobInfo(remoteInfo),
        source: 'range',
        chunkSize: BLOB_RANGE_CHUNK_SIZE,
        fetchRange: (start, endInclusive) =>
          fetchImpl(blobApiPath(workspaceId, key), {
            credentials: 'include',
            headers: {
              ...getBuildVersionHeaders(),
              Range: `bytes=${start}-${endInclusive}`,
            },
          }),
      }
    );
    assertOPFSAvailable();
    return file;
  } catch {
    assertOPFSAvailable();
    return null;
  }
}

export async function resolveWorkspaceBlobFromCacheOrRemote({
  workspaceId,
  key,
  fetchImpl,
}: ResolveWorkspaceBlobOptions): Promise<Blob | null> {
  const localFile = await opfsBlobCache.getCompleteFile(workspaceId, key);
  const remoteFetch = fetchImpl ?? workspaceBlobFetchFactory?.(workspaceId);
  if (localFile) {
    const localInfo = await opfsBlobCache.getInfo(workspaceId, key);
    if (remoteFetch) {
      validateCachedBlobInBackground(workspaceId, key, remoteFetch);
    }
    return restoreBlobMimeType(localFile, localInfo?.mime);
  }

  assertOPFSAvailable();
  if (!remoteFetch) return null;

  const infoResult = await fetchWorkspaceBlobInfo(
    workspaceId,
    key,
    remoteFetch
  );
  if (infoResult.status === 'ok') {
    const { info } = infoResult;
    const cachedFile = await opfsBlobCache.getCompleteFile(workspaceId, key, {
      size: info.size,
      cacheToken: cacheTokenForWorkspaceBlobInfo(info),
    });
    if (cachedFile) return restoreBlobMimeType(cachedFile, info.mimeType);

    const remoteFile = await cacheRemoteBlobIntoOPFS(
      workspaceId,
      key,
      remoteFetch,
      info
    );
    if (remoteFile) return restoreBlobMimeType(remoteFile, info.mimeType);
    return null;
  } else if (infoResult.status === 'terminal') {
    await opfsBlobCache.delete(workspaceId, key);
    return null;
  } else {
    const cachedFile = await opfsBlobCache.getCompleteFile(workspaceId, key);
    if (cachedFile) {
      const localInfo = await opfsBlobCache.getInfo(workspaceId, key);
      return restoreBlobMimeType(cachedFile, localInfo?.mime);
    }
  }
  return null;
}

export async function resolveWorkspaceBlobMetadata({
  workspaceId,
  key,
  fetchImpl,
}: ResolveWorkspaceBlobOptions): Promise<WorkspaceBlobMetadata | null> {
  const localInfo = await opfsBlobCache.getInfo(workspaceId, key);
  if (
    localInfo &&
    typeof localInfo.mime === 'string' &&
    localInfo.mime !== 'application/octet-stream' &&
    Number.isSafeInteger(localInfo.size) &&
    localInfo.size >= 0
  ) {
    return { mime: localInfo.mime, size: localInfo.size };
  }

  const remoteFetch = fetchImpl ?? workspaceBlobFetchFactory?.(workspaceId);
  if (!remoteFetch) return null;
  const result = await fetchWorkspaceBlobInfo(workspaceId, key, remoteFetch);
  return result.status === 'ok'
    ? { mime: result.info.mimeType, size: result.info.size }
    : null;
}
